import { downloadBinaryData } from "@/lib/browser/download";
import { parseFromFile, workbookToBuffer } from "@/lib/excel";
import ExcelJS from "exceljs";
import { useState, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { ValidationError } from "@/lib/responseResults";
import { McCluskyFeeAccrual } from "@lhc/standalone-reports/month_end";

export function McCluskyFeeAccrualDashboard() {
    const { toast } = useToast();
    const [ticketsFile, setTicketsFile] = useState<File | null>(null);
    const [summaryFile, setSummaryFile] = useState<File | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const ticketsFileInputRef = useRef<HTMLInputElement>(null);
    const summaryFileInputRef = useRef<HTMLInputElement>(null);
    const onLoad = async () => {
        if (!ticketsFile || !summaryFile) {
            return;
        } else if (isGenerating) {
            return;
        }

        setIsGenerating(true);
        try {
            const filteredRows = await (async () => {
                const workbook = new ExcelJS.Workbook();
                const bufferResult = await parseFromFile(ticketsFile!);

                if (!bufferResult.success) {
                    throw new Error(bufferResult.error || "Failed to parse tickets file");
                }

                return McCluskyFeeAccrual.filterTicketsFile(bufferResult.data!.excelJsWorkbook)
            })();

            const updatedAccrualWorkbook = await (async () => {
                const bufferResult = await parseFromFile(summaryFile!);

                if (!bufferResult.success) {
                    throw new Error(bufferResult.error || "Failed to parse summary file");
                }

                return McCluskyFeeAccrual.updateAccrualFileWith({ workbook: bufferResult.data!.excelJsWorkbook, filteredRows: filteredRows! })
            })();

            downloadBinaryData(
                await workbookToBuffer(updatedAccrualWorkbook),
                summaryFile?.name || "updated_mcclusky_fee_accrual.xlsx",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            );

            toast({
                title: "Report Generated Successfully"
            });
        } catch (error: any) {
            console.error('Error generating report:', error);
            if (error?.success === false && error?.message) {
                toast({
                    title: "Failed to generate report",
                    description: error.message,
                    variant: "destructive",
                });
                return;
            }

            toast({
                title: "Failed to generate report",
                description: error instanceof Error ? error.message : "An unknown error occurred"
            });
        } finally {
            if (ticketsFileInputRef.current) {
                ticketsFileInputRef.current.value = "";
            }
            if (summaryFileInputRef.current) {
                summaryFileInputRef.current.value = "";
            }
            setTicketsFile(null);
            setSummaryFile(null);
            setIsGenerating(false);
        }
    };

    return (
        <div className="container mx-auto p-6">
            <Card>
                <CardHeader>
                    <CardTitle>McClusky Fee Accrual Report</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="tickets-file">Tickets File (Required)</Label>
                        <Input
                            id="tickets-file"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => setTicketsFile(e.target.files?.[0] || null)}
                            disabled={isGenerating}
                            ref={ticketsFileInputRef}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="summary-file">Summary File (Required)</Label>
                        <Input
                            id="summary-file"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => setSummaryFile(e.target.files?.[0] || null)}
                            disabled={isGenerating}
                            ref={summaryFileInputRef}
                        />
                    </div>

                    <Button 
                        onClick={onLoad} 
                        disabled={!ticketsFile || !summaryFile || isGenerating}
                        className="w-full"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating Report...
                            </>
                        ) : (
                            'Generate Report'
                        )}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
